import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ParentApi } from '../../services/api/ParentApi';

const ParentContext = createContext(null);

// Stable empty array to avoid re-render churn for widgets that render arrays.
const EMPTY_ARRAY = Object.freeze([]);

// Default alert prefs used until a backend endpoint exists to persist them.
const DEFAULT_ALERT_PREFERENCES = {
  emailFrequency: 'weekly',
  pushEnabled: false,
  alertTypes: {
    overdueAssignments: true,
    upcomingDeadlines: true,
    decliningActivity: true,
    positiveMilestones: true,
  },
};

// ─── Mappers: backend DTOs → shapes the parent pages consume ──────────────────

const priorityLabel = (p) => (p >= 3 ? 'high' : p === 2 ? 'medium' : 'low');

const mapAssignment = (dto, courseNameById) => {
  const completed = !!dto.completed;
  const dueRaw = dto.dueDatetime || null;
  const overdue = !completed && dueRaw && new Date(dueRaw) < new Date();
  return {
    id: dto.assignmentId,
    title: dto.title,
    course: courseNameById[dto.endeavorId] || '',
    courseId: dto.endeavorId,
    dueDate: dueRaw ? String(dueRaw).split('T')[0] : null,
    status: completed ? 'completed' : (overdue ? 'overdue' : 'not-started'),
    priority: priorityLabel(dto.priority || 0),
    estimatedEffort: '',
    started: completed,
    studyActivity: 0,
  };
};

const mapCourse = (endeavor, assignments) => {
  const courseAssignments = assignments.filter(a => a.courseId === endeavor.endeavorId);
  const total = courseAssignments.length;
  const completedCount = courseAssignments.filter(a => a.status === 'completed').length;
  const overdue = courseAssignments.filter(a => a.status === 'overdue').length;
  const upcoming = courseAssignments.filter(a => a.status !== 'completed' && a.status !== 'overdue').length;
  return {
    id: endeavor.endeavorId,
    name: endeavor.name,
    instructor: '',
    completionRate: total > 0 ? Math.round((completedCount / total) * 100) : 0,
    upcomingAssignments: upcoming,
    overdueAssignments: overdue,
    recentActivity: 'low',
    trend: 'stable',
    riskLabel: overdue > 0 ? 'watch' : null,
  };
};

// Full activity shape with safe defaults. Analytics like weekly charts / tool
// breakdowns have no backend source yet, so they render as empty states.
const buildActivityShape = (real, mappedAssignments) => ({
  activeDays: Array.isArray(real?.dailyActivity) ? real.dailyActivity.length : 0,
  studySessions: 0,
  totalStudyMinutes: 0,
  quizzesCompleted: 0,
  quizQuestionsAnswered: 0,
  averageQuizScore: 0,
  flashcardSessions: 0,
  flashcardsReviewed: 0,
  notesCreated: 0,
  studyConversations: 0,
  documentsReviewed: 0,
  assignmentsCompleted: mappedAssignments.filter(a => a.status === 'completed').length,
  weeklyData: [],
  byCourse: [],
  byTool: [],
  weeklyTrend: [],
  eventBreakdown: real?.eventBreakdown || [],
  dailyActivity: real?.dailyActivity || [],
});

const mapChildToStudent = (child) => ({
  id: String(child.childUserId),
  childUserId: child.childUserId,
  firstName: child.firstName || '',
  lastName: child.lastName || '',
  email: child.email || '',
  grade: null,
  school: '',
  graduationYear: null,
  avatar: null,
  status: 'active',
  connectionStatus: 'connected',
  lastActive: child.linkedAt ? String(child.linkedAt).split('T')[0] : null,
  overallStatus: 'no-data',
});

const mapInviteToStudent = (invite) => ({
  id: `invite-${invite.id}`,
  inviteId: invite.id,
  firstName: '',
  lastName: '',
  email: invite.childEmail || '',
  status: 'pending',
  connectionStatus: 'pending',
  overallStatus: 'no-data',
  lastActive: null,
});

export function ParentProvider({ children }) {
  const { user, isParent, accessToken } = useAuth();

  const [activeChildren, setActiveChildren] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [childDataCache, setChildDataCache] = useState({}); // childUserId → { courses, assignments, activity }
  const [alertPreferences, setAlertPreferences] = useState(DEFAULT_ALERT_PREFERENCES);
  const [demoState, setDemoState] = useState('normal'); // normal | loading | error | empty
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ─── Children + invites ──────────────────────────────────────────────
  const refreshChildren = useCallback(async () => {
    if (!accessToken || !isParent()) return;
    setLoading(true);
    setError(null);
    try {
      const [childrenResp, invitesResp] = await Promise.all([
        ParentApi.getChildren(),
        ParentApi.getPendingInvites(),
      ]);
      const childrenData = childrenResp.ok ? await childrenResp.json().catch(() => []) : [];
      const invitesData = invitesResp.ok ? await invitesResp.json().catch(() => []) : [];
      const mappedChildren = (Array.isArray(childrenData) ? childrenData : []).map(mapChildToStudent);
      setActiveChildren(mappedChildren);
      setPendingInvites(Array.isArray(invitesData) ? invitesData : []);
      setSelectedStudentId(prev => prev || (mappedChildren[0]?.id ?? null));
    } catch (e) {
      setError(e?.message || 'Failed to load children');
    } finally {
      setLoading(false);
    }
  }, [accessToken, isParent]);

  useEffect(() => {
    refreshChildren();
  }, [refreshChildren]);

  // ─── Per-child data (cached by childUserId) ──────────────────────────
  const loadChildData = useCallback(async (childUserId) => {
    if (childUserId == null) return;
    try {
      const [coursesResp, assignmentsResp, activityResp] = await Promise.all([
        ParentApi.getChildCourses(childUserId),
        ParentApi.getChildAssignments(childUserId),
        ParentApi.getChildActivity(childUserId),
      ]);
      const courses = coursesResp.ok ? await coursesResp.json().catch(() => []) : [];
      const assignmentsPage = assignmentsResp.ok ? await assignmentsResp.json().catch(() => ({})) : {};
      const activity = activityResp.ok ? await activityResp.json().catch(() => ({})) : {};

      const courseNameById = {};
      (Array.isArray(courses) ? courses : []).forEach(c => { courseNameById[c.endeavorId] = c.name; });
      const rawAssignments = Array.isArray(assignmentsPage?.content) ? assignmentsPage.content : [];
      const mappedAssignments = rawAssignments.map(a => mapAssignment(a, courseNameById));
      const mappedCourses = (Array.isArray(courses) ? courses : []).map(c => mapCourse(c, mappedAssignments));

      setChildDataCache(prev => ({
        ...prev,
        [childUserId]: {
          courses: mappedCourses,
          assignments: mappedAssignments,
          activity: buildActivityShape(activity, mappedAssignments),
        },
      }));
    } catch (e) {
      // Leave cache empty on failure; pages fall back to empty states.
      setChildDataCache(prev => ({
        ...prev,
        [childUserId]: { courses: [], assignments: [], activity: buildActivityShape(null, []) },
      }));
    }
  }, []);

  const students = useMemo(
    () => [...activeChildren, ...pendingInvites.map(mapInviteToStudent)],
    [activeChildren, pendingInvites]
  );

  const selectedStudent = useMemo(
    () => students.find(s => s.id === selectedStudentId) || activeChildren[0] || null,
    [students, selectedStudentId, activeChildren]
  );

  const selectedChildUserId = selectedStudent?.childUserId ?? null;

  useEffect(() => {
    if (selectedChildUserId != null && !childDataCache[selectedChildUserId]) {
      loadChildData(selectedChildUserId);
    }
  }, [selectedChildUserId, childDataCache, loadChildData]);

  const switchStudent = useCallback((studentId) => {
    setSelectedStudentId(studentId);
  }, []);

  // ─── Invite actions ──────────────────────────────────────────────────
  const inviteChild = useCallback(async ({ email, firstName, lastName }) => {
    const resp = await ParentApi.inviteChild({ email, firstName, lastName });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data.error || 'Failed to send invite');
    }
    await refreshChildren();
    return data;
  }, [refreshChildren]);

  const revokeInvite = useCallback(async (inviteId) => {
    const resp = await ParentApi.revokeInvite(inviteId);
    if (resp.ok) await refreshChildren();
    return resp.ok;
  }, [refreshChildren]);

  const removeStudent = useCallback(async (student) => {
    // Accepts either a student object or a childUserId.
    const childUserId = typeof student === 'object' ? student?.childUserId : student;
    if (childUserId == null) return false;
    const resp = await ParentApi.unlinkChild(childUserId);
    if (resp.ok) await refreshChildren();
    return resp.ok;
  }, [refreshChildren]);

  const updateAlertPreferences = useCallback((newPrefs) => {
    setAlertPreferences(prev => ({ ...prev, ...newPrefs }));
  }, []);

  // ─── Derived data for the selected child ─────────────────────────────
  const childData = selectedChildUserId != null ? childDataCache[selectedChildUserId] : null;
  const isEmpty = demoState === 'empty';

  const currentCourses = useMemo(
    () => (isEmpty ? [] : (childData?.courses || [])),
    [isEmpty, childData]
  );
  const currentAssignments = useMemo(
    () => (isEmpty ? [] : (childData?.assignments || [])),
    [isEmpty, childData]
  );
  const currentActivity = useMemo(
    () => (isEmpty ? {} : (childData?.activity || buildActivityShape(null, []))),
    [isEmpty, childData]
  );

  // No backend source yet — pages check the `has*` flags below and render
  // <ParentBackendPendingBanner> when false. See src/parent/BACKEND_GAPS.md.
  const currentAlerts = EMPTY_ARRAY;
  const currentReports = EMPTY_ARRAY;
  const currentActivityFeed = EMPTY_ARRAY;
  const allAlerts = EMPTY_ARRAY;
  const hasAlertsBackend = false;
  const hasReportsBackend = false;
  const hasActivityFeedBackend = false;

  const parentProfile = useMemo(() => ({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    name: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
  }), [user]);

  const value = useMemo(() => ({
    // State
    students,
    pendingInvites,
    selectedStudent,
    selectedStudentId: selectedStudent?.id ?? null,
    demoState,
    loading,
    error,
    parentProfile,
    alertPreferences,

    // Current student data
    currentCourses,
    currentAssignments,
    currentActivity,
    currentAlerts,
    currentReports,
    currentActivityFeed,
    allAlerts,
    hasAlertsBackend,
    hasReportsBackend,
    hasActivityFeedBackend,

    // Actions
    switchStudent,
    refreshChildren,
    inviteChild,
    revokeInvite,
    removeStudent,
    updateAlertPreferences,
    setDemoState,
  }), [
    students, pendingInvites, selectedStudent, demoState, loading, error,
    parentProfile, alertPreferences, currentCourses, currentAssignments,
    currentActivity, currentAlerts, currentReports, currentActivityFeed, allAlerts,
    hasAlertsBackend, hasReportsBackend, hasActivityFeedBackend,
    switchStudent, refreshChildren, inviteChild, revokeInvite, removeStudent,
    updateAlertPreferences,
  ]);

  return (
    <ParentContext.Provider value={value}>
      {children}
    </ParentContext.Provider>
  );
}

export function useParent() {
  const context = useContext(ParentContext);
  if (!context) {
    throw new Error('useParent must be used within a ParentProvider');
  }
  return context;
}

export default ParentContext;
